import React, { CSSProperties, PropsWithChildren } from 'react'
import { AlignItems, FlexDirection, JustifyContent, Spacing } from '../helperTypes'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /**
   * Defines the `align-items` style property.
   * @default stretch
   */
  alignItems?: AlignItems
  /**
   * Styles applied to the children wrappers.
   */
  childrenStyle?: CSSProperties
  /**
   * Defines the `flex-direction` style property.
   * @default row
   */
  direction?: FlexDirection
  /**
   * Defines the `justify-content` style property.
   * @default start
   */
  justify?: JustifyContent
  /**
   * Defines the `position` style property.
   * @default static
   */
  position?: 'absolute' | 'relative' | 'static'
  /**
   * Defines the `gap` style property.
   */
  spacing?: Spacing
  /**
   * Defines the `flex-wrap` style property.
   * @default false
   */
  wrap?: boolean
}>

export type FlexProps = Props & CommonProps

// TODEPRECATE
const spacingMap = new Map([
  ['quarter-x', 2],
  ['half-x', 4],
  ['three-quarter-x', 6],
  ['1-x', 8],
  ['1-and-a-half-x', 12],
  ['2-x', 16],
  ['3-x', 24],
  ['4-x', 32],
  ['5-x', 40],
  ['8-x', 64],
])

const getGap = (spacing: Spacing | undefined): number => {
  const value = spacing && spacingMap.get(spacing)
  return value ? value / 2 : 0
}

const getFlexPropertyValue = (propValue: string): string => {
  switch (propValue) {
    case 'start':
      return 'flex-start'
    case 'end':
      return 'flex-end'
    default:
      return propValue
  }
}

const extractStylesFromProps = (props: FlexProps): [CSSProperties, any] => {
  const { position, direction, wrap, justify, alignItems, spacing, style, ...rest } = props

  return [
    {
      display: 'flex',
      position: position || 'static',
      boxSizing: 'border-box',
      flexDirection: direction || 'row',
      flexWrap: wrap ? 'wrap' : 'nowrap',
      justifyContent: getFlexPropertyValue(justify || 'start'),
      alignItems: getFlexPropertyValue(alignItems || 'stretch'),
      margin: `-${getGap(spacing)}px`,
      ...style,
    },
    rest,
  ]
}

const getStyleForChildren = (props: Props): CSSProperties => ({
  padding: props.spacing ? `${getGap(props.spacing)}px` : 0,
})

const Flex = ({ className, children, childrenStyle, ...restProps }: FlexProps) => {
  const [style, rest] = extractStylesFromProps(restProps)
  return (
    <div className={className} style={style} {...rest}>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child)) {
          return null
        }
        if (child.type === React.Fragment) {
          console.warn("The Flex component doesn't accept a Fragment as a child. Consider providing an array instead.")
        }
        return !restProps.spacing ? (
          child
        ) : (
          <div style={{ ...getStyleForChildren(restProps), ...childrenStyle }}>{child}</div>
        )
      })}
    </div>
  )
}

export default Flex
